import { useState, type ReactNode } from 'react';
import { CircleAlert, Tag as TagIcon } from 'lucide-react';
import { Button, Checkbox, Dialog, EmptyState, Skeleton, TagChip } from '@/components/ui';
import { useSetSiteTags } from '@/hooks/useSites';
import { useTags } from '@/hooks/useTags';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

/**
 * Which tags a site carries.
 *
 * The whole set is sent on save rather than one add and one remove per click:
 * the operator is describing the end state, and a half-applied change is worse
 * than a slower one.
 */
export function SiteTagsDialog({
  open,
  site,
  onClose,
}: {
  readonly open: boolean;
  readonly site: SiteDetail;
  readonly onClose: () => void;
}): ReactNode {
  const tags = useTags({ enabled: open });
  const [selected, setSelected] = useState<readonly string[]>(site.tags.map((tag) => tag.id));

  const save = useSetSiteTags(site.id, {
    onSuccess: () => {
      toast.success('Теги сохранены');
      onClose();
    },
  });

  const toggle = (id: string): void => {
    setSelected((current) =>
      current.includes(id) ? current.filter((tagId) => tagId !== id) : [...current, id],
    );
  };

  const items = tags.data ?? [];

  return (
    <Dialog
      open={open}
      title="Теги сайта"
      description={site.displayName}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate([...selected])}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="stack">
        {tags.isPending ? (
          <div className="stack stack-tight">
            <Skeleton height={22} />
            <Skeleton height={22} />
            <Skeleton height={22} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={TagIcon}
            message="Тегов пока нет. Теги создаются в списке сайтов."
          />
        ) : (
          <div className="stack stack-tight">
            {items.map((tag) => (
              <Checkbox
                key={tag.id}
                checked={selected.includes(tag.id)}
                onChange={() => toggle(tag.id)}
                label={<TagChip tag={tag} size="sm" />}
              />
            ))}
          </div>
        )}

        {save.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(save.error, 'Не удалось сохранить теги')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
